import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Relationships Manager
 *
 * Three nested views over the `relationships` table (parent_table = accounts):
 *   1) Types — INSTRUCTOR_CONCENTRATION / INSTRUCTOR_ASSOCIATION / TEACHER_OF_RECORD
 *   2) Parents — accounts that currently have at least one link of the chosen type
 *   3) Web — SVG diagram with the chosen account at the center, webbed to each
 *      child (taxonomy / association / enrollment-via-student). Children expose
 *      hover delete; the center account exposes a "+" affordance to add new
 *      connections from a searchable picker.
 *
 * Backed by /api/relationships/* (see api/relationships.js).
 */

const RelationshipsManager = ({ userData }) => {
  const [view, setView] = useState('types'); // 'types' | 'parents' | 'web'
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [activeAccount, setActiveAccount] = useState(null);

  const [parents, setParents] = useState([]);
  const [parentSearch, setParentSearch] = useState('');

  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddParentModal, setShowAddParentModal] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get('/api/relationships/types', authHeaders);
        if (alive) setTypes(res.data.types || []);
      } catch (err) {
        console.error('Failed to load relationship types', err);
        if (alive) setError('Failed to load relationship types.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [authHeaders]);

  const refreshTypeCounts = async () => {
    try {
      const res = await axios.get('/api/relationships/types', authHeaders);
      setTypes(res.data.types || []);
    } catch (err) {
      console.error('Failed to refresh relationship types', err);
    }
  };

  const fetchParents = async (type) => {
    try {
      setLoading(true);
      const res = await axios.get('/api/relationships/parents', { ...authHeaders, params: { type } });
      setParents(res.data.parents || []);
    } catch (err) {
      console.error('Failed to load parents', err);
      setError('Failed to load accounts for this type.');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnections = async (type, parentId) => {
    try {
      setLoadingConnections(true);
      const res = await axios.get('/api/relationships/children', {
        ...authHeaders,
        params: { type, parentId }
      });
      setConnections(res.data.children || []);
    } catch (err) {
      console.error('Failed to load connections', err);
      setError('Failed to load connections for this account.');
    } finally {
      setLoadingConnections(false);
    }
  };

  const goToType = async (typeRow) => {
    setActiveType(typeRow);
    setActiveAccount(null);
    setView('parents');
    setParentSearch('');
    await fetchParents(typeRow.type);
  };

  const goToAccount = async (account) => {
    setActiveAccount(account);
    setView('web');
    await fetchConnections(activeType.type, account.account_id);
  };

  const goBackToTypes = () => {
    setView('types');
    setActiveType(null);
    setActiveAccount(null);
    setParents([]);
    setConnections([]);
    refreshTypeCounts();
  };

  const goBackToParents = async () => {
    setView('parents');
    setActiveAccount(null);
    setConnections([]);
    if (activeType) await fetchParents(activeType.type);
  };

  const handleDeleteRelationship = async (relationshipId) => {
    if (!window.confirm('Remove this connection?')) return;
    try {
      await axios.delete(`/api/relationships/${relationshipId}`, authHeaders);
      setConnections((prev) => prev.filter((c) => c.relationship_id !== relationshipId));
    } catch (err) {
      console.error('Failed to remove connection', err);
      alert(err.response?.data?.error || 'Failed to remove connection.');
    }
  };

  const handleAddRelationship = async (childId) => {
    try {
      await axios.post(
        '/api/relationships',
        {
          type: activeType.type,
          parentId: activeAccount.account_id,
          childId
        },
        authHeaders
      );
      setShowAddModal(false);
      await fetchConnections(activeType.type, activeAccount.account_id);
    } catch (err) {
      console.error('Failed to add connection', err);
      alert(err.response?.data?.error || 'Failed to add connection.');
    }
  };

  const handleAddNewParent = async (account) => {
    setShowAddParentModal(false);
    setActiveAccount({
      account_id: account.account_id,
      first_name: account.first_name,
      last_name: account.last_name,
      primary_email: account.primary_email,
      connections: 0
    });
    setView('web');
    setConnections([]);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Relationships</h1>
          <div style={styles.subtitle}>
            Browse and manage account-to-entity relationships used across the platform.
          </div>
        </div>
        <Breadcrumbs
          view={view}
          activeType={activeType}
          activeAccount={activeAccount}
          onTypes={goBackToTypes}
          onParents={goBackToParents}
        />
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {loading && view === 'types' ? (
        <div style={styles.statePad}>Loading…</div>
      ) : view === 'types' ? (
        <TypesView types={types} onSelect={goToType} />
      ) : view === 'parents' ? (
        <ParentsView
          activeType={activeType}
          parents={parents}
          loading={loading}
          search={parentSearch}
          onSearch={setParentSearch}
          onSelect={goToAccount}
          onAddParent={() => setShowAddParentModal(true)}
        />
      ) : (
        <WebView
          activeType={activeType}
          activeAccount={activeAccount}
          connections={connections}
          loading={loadingConnections}
          onAdd={() => setShowAddModal(true)}
          onDelete={handleDeleteRelationship}
        />
      )}

      {showAddModal && activeType && activeAccount && (
        <AddConnectionModal
          activeType={activeType}
          parentId={activeAccount.account_id}
          authHeaders={authHeaders}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddRelationship}
        />
      )}

      {showAddParentModal && activeType && (
        <AddParentModal
          activeType={activeType}
          authHeaders={authHeaders}
          onClose={() => setShowAddParentModal(false)}
          onPick={handleAddNewParent}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Subviews

const Breadcrumbs = ({ view, activeType, activeAccount, onTypes, onParents }) => (
  <div style={styles.crumbs}>
    <button type="button" onClick={onTypes} style={styles.crumbButton}>Types</button>
    {activeType && (
      <>
        <span style={styles.crumbSep}>/</span>
        <button
          type="button"
          onClick={view === 'web' ? onParents : undefined}
          style={view === 'web' ? styles.crumbButton : styles.crumbCurrent}
          disabled={view !== 'web'}
        >
          {activeType.label}
        </button>
      </>
    )}
    {activeAccount && (
      <>
        <span style={styles.crumbSep}>/</span>
        <span style={styles.crumbCurrent}>
          {accountDisplayName(activeAccount)}
        </span>
      </>
    )}
  </div>
);

const TypesView = ({ types, onSelect }) => (
  <div style={styles.typesGrid}>
    {types.map((t) => (
      <button
        key={t.type}
        type="button"
        style={styles.typeCard}
        onClick={() => onSelect(t)}
      >
        <div style={styles.typeCardHead}>
          <div style={styles.typeCardLabel}>{t.label}</div>
          <span style={styles.tag}>{t.type}</span>
        </div>
        <div style={styles.typeCardDesc}>{t.description}</div>
        <div style={styles.typeCardStats}>
          <div style={styles.statBlock}>
            <div style={styles.statValue}>{t.parents}</div>
            <div style={styles.statLabel}>Accounts</div>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statBlock}>
            <div style={styles.statValue}>{t.total}</div>
            <div style={styles.statLabel}>Connections</div>
          </div>
        </div>
      </button>
    ))}
  </div>
);

const ParentsView = ({
  activeType,
  parents,
  loading,
  search,
  onSearch,
  onSelect,
  onAddParent
}) => {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parents;
    return parents.filter((p) => {
      const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
      const email = (p.primary_email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [parents, search]);

  const [hoverId, setHoverId] = useState(null);

  return (
    <section style={styles.panel}>
      <div style={styles.panelTopBar}>
        <div>
          <div style={styles.panelEyebrow}>
            {activeType.label} <span style={styles.count}>{filtered.length}</span>
          </div>
          <div style={styles.panelHint}>Click a row to open the relationship web.</div>
        </div>
        <div style={styles.toolbarRight}>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by name or email…"
            style={styles.searchInput}
          />
          <button type="button" style={styles.primaryButton} onClick={onAddParent}>
            New account link
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.statePad}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          {parents.length === 0
            ? 'No accounts have this relationship yet.'
            : 'No matching accounts.'}
        </div>
      ) : (
        <div style={styles.regTable}>
          <div style={styles.regHeader}>
            <div>Account</div>
            <div>Email</div>
            <div style={styles.alignRight}>Connections</div>
          </div>
          {filtered.map((p) => {
            const isHover = hoverId === p.account_id;
            return (
              <div
                key={p.account_id}
                role="button"
                tabIndex={0}
                style={{
                  ...styles.regRowButton,
                  ...(isHover ? styles.regRowButtonHover : {})
                }}
                onClick={() => onSelect(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(p);
                  }
                }}
                onMouseEnter={() => setHoverId(p.account_id)}
                onMouseLeave={() => setHoverId((h) => (h === p.account_id ? null : h))}
              >
                <div style={styles.regStudent}>{accountDisplayName(p)}</div>
                <div style={{ ...styles.regCell, ...styles.muted, ...styles.ellipsis }}>
                  {p.primary_email || '—'}
                </div>
                <div style={{ ...styles.regCell, ...styles.alignRight }}>
                  <span style={styles.tag}>{p.connections}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SVG Web diagram

const WebView = ({ activeType, activeAccount, connections, loading, onAdd, onDelete }) => {
  const [mode, setMode] = useState('web'); // 'web' | 'list'

  return (
    <section style={styles.panel}>
      <div style={styles.panelTopBar}>
        <div style={styles.tabsGroup}>
          <div style={styles.tabs}>
            <button
              type="button"
              style={{ ...styles.tab, ...(mode === 'web' ? styles.tabActive : {}) }}
              onClick={() => setMode('web')}
            >
              Web <span style={styles.tabCount}>{connections.length}</span>
            </button>
            <button
              type="button"
              style={{ ...styles.tab, ...(mode === 'list' ? styles.tabActive : {}) }}
              onClick={() => setMode('list')}
            >
              List View
            </button>
          </div>
          {mode === 'list' && activeAccount && (
            <span style={styles.parentBadge} title={activeAccount.primary_email || ''}>
              <span style={styles.parentBadgeDot} />
              <span style={styles.parentBadgeLabel}>
                {accountDisplayName(activeAccount)}
              </span>
            </span>
          )}
          {loading && <span style={styles.loadingText}>loading…</span>}
        </div>
        <div style={styles.legend}>
          {mode === 'web' ? (
            <span style={styles.legendItem}>Hover a child to remove · Click + on the center to add</span>
          ) : (
            <button type="button" style={styles.ghostButton} onClick={onAdd}>
              + Add connection
            </button>
          )}
        </div>
      </div>

      {mode === 'web' ? (
        <RelationshipWeb
          activeType={activeType}
          activeAccount={activeAccount}
          connections={connections}
          onAdd={onAdd}
          onDelete={onDelete}
        />
      ) : (
        <ConnectionsList
          activeType={activeType}
          connections={connections}
          onDelete={onDelete}
        />
      )}
    </section>
  );
};

const ConnectionsList = ({ activeType, connections, onDelete }) => {
  if (connections.length === 0) {
    return (
      <div style={styles.emptyState}>
        No connections yet. Use <span style={styles.kbd}>+ Add connection</span> to add one.
      </div>
    );
  }
  const childKindLabel =
    activeType?.childKind === 'concentration' ? 'Concentration'
      : activeType?.childKind === 'association' ? 'FA Code'
        : activeType?.childKind === 'enrollment' ? 'Student'
          : 'Item';

  return (
    <div style={styles.regTable}>
      <div style={{ ...styles.regHeader, ...styles.listHeaderGrid }}>
        <div>{childKindLabel}</div>
        <div>Detail</div>
        <div style={styles.alignRight}>Actions</div>
      </div>
      {connections.map((c) => (
        <div key={c.relationship_id} style={{ ...styles.regRowButton, ...styles.listRowGrid, cursor: 'default' }}>
          <div style={styles.regStudent}>{c.label || '(missing)'}</div>
          <div style={{ ...styles.regCell, ...styles.muted, ...styles.ellipsis }}>
            {c.sublabel || '—'}
          </div>
          <div style={{ ...styles.regCell, ...styles.alignRight }}>
            <button type="button" style={styles.dangerButton} onClick={() => onDelete(c.relationship_id)}>
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const RelationshipWeb = ({ activeType, activeAccount, connections, onAdd, onDelete }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(880);
  const [hoverId, setHoverId] = useState(null);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth || 880);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const n = Math.max(connections.length, 1);
  const minHeight = 420;
  const radius = Math.max(140, Math.min(width / 2 - 110, 80 + n * 12));
  const computedHeight = Math.max(minHeight, radius * 2 + 160);
  const cx = width / 2;
  const cy = computedHeight / 2;

  const centerW = 220;
  const centerH = 64;
  const childW = 168;
  const childH = 48;

  const childPositions = connections.map((_, i) => {
    const angle = (-Math.PI / 2) + (2 * Math.PI * i) / Math.max(connections.length, 1);
    return {
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius
    };
  });

  const accountTitle = accountDisplayName(activeAccount);

  return (
    <div ref={containerRef} style={styles.webWrap}>
      {connections.length === 0 && !accountTitle ? null : (
        <svg width="100%" height={computedHeight} viewBox={`0 0 ${width} ${computedHeight}`} style={styles.webSvg}>
          {/* Connection lines */}
          {childPositions.map((p, i) => {
            const c = connections[i];
            const isHover = hoverId === c.relationship_id;
            return (
              <line
                key={`line-${c.relationship_id}`}
                x1={cx}
                y1={cy}
                x2={p.x}
                y2={p.y}
                stroke={isHover ? '#94a3b8' : '#cbd5e1'}
                strokeWidth={isHover ? 1.5 : 1}
                strokeDasharray={isHover ? 'none' : '3 3'}
              />
            );
          })}

          {/* Center account node */}
          <g transform={`translate(${cx - centerW / 2}, ${cy - centerH / 2})`}>
            <rect
              width={centerW}
              height={centerH}
              rx={10}
              ry={10}
              fill="#0f172a"
              stroke="#0f172a"
            />
            <text
              x={centerW / 2}
              y={centerH / 2 - 4}
              textAnchor="middle"
              fontSize="13"
              fontWeight="600"
              fill="#ffffff"
              fontFamily={fontStack}
            >
              {truncate(accountTitle, 28)}
            </text>
            <text
              x={centerW / 2}
              y={centerH / 2 + 14}
              textAnchor="middle"
              fontSize="10"
              fill="#cbd5e1"
              fontFamily={fontStack}
            >
              {truncate(activeAccount?.primary_email || '', 32)}
            </text>
          </g>

          {/* + button at top-right of center box */}
          <g
            transform={`translate(${cx + centerW / 2 - 10}, ${cy - centerH / 2 - 10})`}
            style={{ cursor: 'pointer' }}
            onClick={onAdd}
          >
            <circle r={12} fill="#ffffff" stroke="#0f172a" strokeWidth={1.4} />
            <line x1={-5} y1={0} x2={5} y2={0} stroke="#0f172a" strokeWidth={1.6} strokeLinecap="round" />
            <line x1={0} y1={-5} x2={0} y2={5} stroke="#0f172a" strokeWidth={1.6} strokeLinecap="round" />
          </g>

          {/* Child nodes */}
          {connections.map((c, i) => {
            const p = childPositions[i];
            const isHover = hoverId === c.relationship_id;
            const label = truncate(c.label || '(missing)', 22);
            const sublabel = c.sublabel ? truncate(c.sublabel, 26) : '';
            return (
              <g
                key={`child-${c.relationship_id}`}
                transform={`translate(${p.x - childW / 2}, ${p.y - childH / 2})`}
                onMouseEnter={() => setHoverId(c.relationship_id)}
                onMouseLeave={() => setHoverId((h) => (h === c.relationship_id ? null : h))}
                style={{ cursor: 'default' }}
              >
                <rect
                  width={childW}
                  height={childH}
                  rx={8}
                  ry={8}
                  fill={isHover ? '#f1f5f9' : '#ffffff'}
                  stroke={isHover ? '#94a3b8' : '#cbd5e1'}
                  strokeWidth={1}
                />
                <text
                  x={childW / 2}
                  y={sublabel ? childH / 2 - 2 : childH / 2 + 4}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="500"
                  fill="#0f172a"
                  fontFamily={fontStack}
                >
                  {label}
                </text>
                {sublabel && (
                  <text
                    x={childW / 2}
                    y={childH / 2 + 13}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#64748b"
                    fontFamily={fontStack}
                  >
                    {sublabel}
                  </text>
                )}
                {/* Delete button revealed on hover */}
                {isHover && (
                  <g
                    transform={`translate(${childW - 10}, -10)`}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(c.relationship_id);
                    }}
                  >
                    <circle r={10} fill="#ffffff" stroke="#ef4444" strokeWidth={1.2} />
                    <line x1={-4} y1={-4} x2={4} y2={4} stroke="#ef4444" strokeWidth={1.4} strokeLinecap="round" />
                    <line x1={-4} y1={4} x2={4} y2={-4} stroke="#ef4444" strokeWidth={1.4} strokeLinecap="round" />
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {connections.length === 0 && (
        <div style={styles.emptyWebHint}>
          No connections yet. Click the <span style={styles.kbd}>+</span> on the center account to add one.
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add modals

const AddConnectionModal = ({ activeType, parentId, authHeaders, onClose, onAdd }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get('/api/relationships/available-children', {
          ...authHeaders,
          params: { type: activeType.type, parentId }
        });
        if (alive) setItems(res.data.children || []);
      } catch (err) {
        console.error('Failed to load options', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [activeType, parentId, authHeaders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      `${i.label || ''} ${i.sublabel || ''}`.toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>Add {activeType.label.toLowerCase()} connection</div>
          <button type="button" style={styles.ghostButton} onClick={onClose}>Close</button>
        </div>
        <div style={styles.modalBody}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${activeType.childKind || 'item'}…`}
            style={styles.searchInput}
            autoFocus
          />

          <div style={styles.pickList}>
            {loading ? (
              <div style={styles.statePad}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={styles.emptyState}>
                {items.length === 0 ? 'Nothing left to add.' : 'No matching items.'}
              </div>
            ) : (
              filtered.map((it) => (
                <button
                  key={it.child_id}
                  type="button"
                  style={styles.pickRow}
                  onClick={() => onAdd(it.child_id)}
                >
                  <div style={styles.pickRowLabel}>{it.label || '(unnamed)'}</div>
                  {it.sublabel && <div style={styles.pickRowSub}>{it.sublabel}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AddParentModal = ({ activeType, authHeaders, onClose, onPick }) => {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get('/api/relationships/available-parents', {
          ...authHeaders,
          params: { type: activeType.type }
        });
        if (alive) setAccounts(res.data.accounts || []);
      } catch (err) {
        console.error('Failed to load accounts', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [activeType, authHeaders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      const name = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
      const email = (a.primary_email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [accounts, search]);

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>Pick an account to manage</div>
          <button type="button" style={styles.ghostButton} onClick={onClose}>Close</button>
        </div>
        <div style={styles.modalBody}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search accounts by name or email…"
            style={styles.searchInput}
            autoFocus
          />

          <div style={styles.pickList}>
            {loading ? (
              <div style={styles.statePad}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={styles.emptyState}>No matching accounts.</div>
            ) : (
              filtered.slice(0, 200).map((a) => (
                <button
                  key={a.account_id}
                  type="button"
                  style={styles.pickRow}
                  onClick={() => onPick(a)}
                >
                  <div style={styles.pickRowLabel}>{accountDisplayName(a)}</div>
                  {a.primary_email && <div style={styles.pickRowSub}>{a.primary_email}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers

const accountDisplayName = (acc) => {
  if (!acc) return '';
  const fn = acc.first_name || '';
  const ln = acc.last_name || '';
  const name = `${fn} ${ln}`.trim();
  return name || acc.primary_email || acc.account_id;
};

const truncate = (s, n) => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

// ─────────────────────────────────────────────────────────────────────────────
// Styles

const colors = {
  text: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  borderStrong: '#cbd5e1',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  surfaceHover: '#f1f5f9',
  danger: '#b91c1c',
  dangerBorder: '#fecaca',
  errorText: '#991b1b',
  errorBg: '#fef2f2',
  errorBorder: '#fecaca'
};

const fontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif';

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '20px auto',
    padding: '16px',
    backgroundColor: colors.surface,
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    fontFamily: fontStack,
    color: colors.text
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '12px'
  },
  title: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: colors.text,
    margin: 0,
    letterSpacing: '-0.01em'
  },
  subtitle: {
    fontSize: '0.78rem',
    color: colors.textSecondary,
    marginTop: '4px',
    maxWidth: '640px',
    lineHeight: 1.45
  },
  statePad: {
    padding: '24px',
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: '0.85rem'
  },
  error: {
    padding: '7px 10px',
    backgroundColor: colors.errorBg,
    color: colors.errorText,
    borderRadius: '6px',
    marginBottom: '8px',
    border: `1px solid ${colors.errorBorder}`,
    fontSize: '0.8rem'
  },
  // Breadcrumbs
  crumbs: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.78rem',
    color: colors.textSecondary,
    flexShrink: 0,
    marginTop: '2px'
  },
  crumbButton: {
    padding: '4px 8px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    color: colors.textSecondary,
    fontSize: '0.78rem',
    cursor: 'pointer',
    fontFamily: fontStack
  },
  crumbCurrent: {
    padding: '4px 8px',
    color: colors.text,
    fontSize: '0.78rem',
    fontWeight: 500,
    background: 'transparent',
    border: '1px solid transparent',
    fontFamily: fontStack
  },
  crumbSep: {
    color: colors.textMuted
  },
  // Types view
  typesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '10px'
  },
  typeCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '14px',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: fontStack,
    color: colors.text
  },
  typeCardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  typeCardLabel: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: colors.text
  },
  typeCardDesc: {
    fontSize: '0.78rem',
    color: colors.textSecondary,
    lineHeight: 1.45
  },
  typeCardStats: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '4px',
    backgroundColor: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '8px 10px',
    gap: '12px'
  },
  statBlock: {
    flex: 1,
    minWidth: 0
  },
  statValue: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.text
  },
  statLabel: {
    fontSize: '0.7rem',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  statDivider: {
    width: '1px',
    height: '24px',
    backgroundColor: colors.border
  },
  // Panels (parents + web)
  panel: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    padding: '12px'
  },
  panelTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '8px',
    minHeight: '24px',
    flexWrap: 'wrap'
  },
  panelEyebrow: {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: colors.textMuted,
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  count: {
    display: 'inline-block',
    minWidth: '18px',
    padding: '0 6px',
    marginLeft: '4px',
    borderRadius: '999px',
    backgroundColor: colors.surfaceHover,
    color: colors.textSecondary,
    fontSize: '0.7rem',
    fontWeight: 600,
    textAlign: 'center',
    letterSpacing: 0
  },
  loadingText: {
    fontSize: '0.72rem',
    color: colors.textMuted,
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: 0,
    marginLeft: '4px'
  },
  legend: {
    fontSize: '0.72rem',
    color: colors.textMuted
  },
  legendItem: {
    color: colors.textMuted
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  searchInput: {
    flex: '1 1 auto',
    minWidth: '220px',
    maxWidth: '420px',
    padding: '6px 10px',
    fontSize: '0.8rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    backgroundColor: colors.surface,
    color: colors.text,
    outline: 'none',
    fontFamily: fontStack,
    boxSizing: 'border-box'
  },
  primaryButton: {
    padding: '6px 10px',
    backgroundColor: colors.text,
    color: '#fff',
    border: `1px solid ${colors.text}`,
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  ghostButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  dangerButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: colors.danger,
    border: `1px solid ${colors.dangerBorder}`,
    borderRadius: '6px',
    fontSize: '0.72rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  // Tabs
  tabsGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  tabs: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    backgroundColor: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '2px'
  },
  parentBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 9px 3px 7px',
    backgroundColor: colors.text,
    color: '#ffffff',
    border: `1px solid ${colors.text}`,
    borderRadius: '999px',
    fontSize: '0.74rem',
    fontWeight: 500,
    maxWidth: '220px'
  },
  parentBadgeDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '999px',
    backgroundColor: '#94a3b8',
    flexShrink: 0
  },
  parentBadgeLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  tab: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    border: 0,
    borderRadius: '6px',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  tabActive: {
    backgroundColor: colors.surface,
    color: colors.text,
    boxShadow: '0 1px 1px rgba(15, 23, 42, 0.06)'
  },
  tabCount: {
    display: 'inline-block',
    minWidth: '16px',
    padding: '0 5px',
    borderRadius: '999px',
    backgroundColor: colors.surfaceHover,
    color: colors.textSecondary,
    fontSize: '0.68rem',
    fontWeight: 600,
    textAlign: 'center'
  },
  // List view inside Web panel
  listHeaderGrid: {
    gridTemplateColumns: 'minmax(180px, 1.4fr) minmax(200px, 1.6fr) 110px'
  },
  listRowGrid: {
    gridTemplateColumns: 'minmax(180px, 1.4fr) minmax(200px, 1.6fr) 110px'
  },
  emptyState: {
    padding: '14px',
    color: colors.textMuted,
    textAlign: 'center',
    background: colors.surfaceAlt,
    border: `1px dashed ${colors.border}`,
    borderRadius: '8px',
    fontSize: '0.8rem'
  },
  // Table for parents view
  regTable: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: colors.surface
  },
  regHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 1.2fr) minmax(200px, 1.8fr) 110px',
    gap: '10px',
    padding: '7px 10px',
    backgroundColor: colors.surfaceAlt,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: colors.textMuted
  },
  regRowButton: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 1.2fr) minmax(200px, 1.8fr) 110px',
    gap: '10px',
    padding: '8px 10px',
    fontSize: '0.82rem',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: colors.surface,
    borderTop: `1px solid ${colors.border}`,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: fontStack,
    color: colors.text,
    outline: 'none',
    userSelect: 'none',
    transition: 'background-color 0.12s ease'
  },
  regRowButtonHover: {
    backgroundColor: colors.surfaceHover
  },
  panelHint: {
    fontSize: '0.72rem',
    color: colors.textMuted,
    marginTop: '2px'
  },
  regStudent: {
    fontWeight: 500,
    color: colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  regCell: {
    color: colors.textSecondary,
    minWidth: 0
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  alignRight: { textAlign: 'right', justifyContent: 'flex-end' },
  muted: { color: colors.textMuted },
  tag: {
    display: 'inline-block',
    padding: '1px 7px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: colors.textSecondary,
    backgroundColor: colors.surfaceHover,
    border: `1px solid ${colors.border}`,
    borderRadius: '999px',
    whiteSpace: 'nowrap'
  },
  // Web diagram
  webWrap: {
    position: 'relative',
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    backgroundColor: colors.surfaceAlt,
    backgroundImage:
      'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.06) 1px, transparent 0)',
    backgroundSize: '14px 14px',
    overflow: 'hidden'
  },
  webSvg: {
    display: 'block'
  },
  emptyWebHint: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '10px',
    textAlign: 'center',
    fontSize: '0.78rem',
    color: colors.textMuted
  },
  kbd: {
    display: 'inline-block',
    padding: '0 5px',
    fontSize: '0.7rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    backgroundColor: colors.surface,
    color: colors.text,
    fontFamily: fontStack,
    fontWeight: 600
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.32)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '64px 16px 16px'
  },
  modalCard: {
    width: 'min(560px, 100%)',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface,
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
    overflow: 'hidden',
    fontFamily: fontStack
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.surface
  },
  modalTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: colors.text
  },
  modalBody: {
    padding: '12px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  pickList: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: colors.surface,
    maxHeight: '52vh',
    overflowY: 'auto'
  },
  pickRow: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    background: colors.surface,
    border: 0,
    borderTop: `1px solid ${colors.border}`,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  pickRowLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: colors.text
  },
  pickRowSub: {
    fontSize: '0.75rem',
    color: colors.textMuted,
    marginTop: '2px'
  }
};

export default RelationshipsManager;
